import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import PageBanner from 'LuxKit/Banners/PageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import switchFunc from 'lib/function/switchFunc'
import { getDismissedBannerIds, isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import NotificationBannerModal from './NotificationBannerModal'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isBannerMessagePayload } from 'lib/lab/dynamicPayload'
import moment from 'moment'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchLabHeaderBanners } from 'actions/SystemActions'
import GeoContext from 'contexts/geoContext'
import { removeRegionCode } from 'lib/string/regionUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getHeaderBannerPresentation, getRenderableHeaderBanner, getTakeoverBannerLocation } from './NotificationBannerV3.utils'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { getImageUrl, getSimpleImageUrl } from 'lib/image/imageUtils'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { renderHeaderBannerRichText } from './headerBannerRichText'

const getBannerKind = switchFunc<React.ComponentProps<typeof PageBanner>['kind'], App.LabBannerMessagePayload['bannerType']>({
  warning: 'warning',
  error: 'critical',
  success: 'promotional-primary',
}, 'informational-primary')

const getBannerIcon = switchFunc<React.ComponentProps<typeof PageBanner>['icon'], App.LabBannerMessagePayload['bannerType']>({
  warning: <LineExclamationTriangleIcon />,
  error: <LineExclamationTriangleIcon />,
  success: <LineCheckCircleIcon />,
}, <LineInfoCircleIcon />)

const BANNER_DISMISSAL_PERIOD_DAYS = 7
const DEFAULT_HEADER_BANNER_BACKGROUND = '#CAFFEB'

const HeaderBannerPageBanner = styled(PageBanner)<{
  $backgroundMode: 'solid' | 'gradient' | 'image'
  $backgroundValue: string
  $textColor: string
  $isCustomAppearance: boolean
}>`
  /* Slightly reduce copy size for header banners to tighten visual density. */
  && .variant-medium {
    font-size: 12px;
    line-height: 17px;
  }

  > div > :first-child {
    align-items: center;
  }

  &&.kind-promotional-primary {
    ${({ $isCustomAppearance, $textColor, $backgroundMode, $backgroundValue }) => {
    if (!$isCustomAppearance) {
      return css``
    }
    if ($backgroundMode === 'image') {
      return css`
          color: ${$textColor};
          background-color: ${DEFAULT_HEADER_BANNER_BACKGROUND};
          background-image: url(${$backgroundValue});
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;

          button.kind-tertiary[aria-controls='notification-page-banner'] {
            --button-color: ${$textColor};
            --button-hover-color: ${$textColor};
            --button-focus-color: ${$textColor};
            --button-active-color: ${$textColor};
            --button-focus-ring-color: ${$textColor};
          }
        `
    }
    if ($backgroundMode === 'gradient') {
      return css`
          color: ${$textColor};
          background-color: ${DEFAULT_HEADER_BANNER_BACKGROUND};
          background-image: ${$backgroundValue};

          button.kind-tertiary[aria-controls='notification-page-banner'] {
            --button-color: ${$textColor};
            --button-hover-color: ${$textColor};
            --button-focus-color: ${$textColor};
            --button-active-color: ${$textColor};
            --button-focus-ring-color: ${$textColor};
          }
        `
    }
    return css`
        color: ${$textColor};
        background-color: ${$backgroundValue};

        button.kind-tertiary[aria-controls='notification-page-banner'] {
          --button-color: ${$textColor};
          --button-hover-color: ${$textColor};
          --button-focus-color: ${$textColor};
          --button-active-color: ${$textColor};
          --button-focus-ring-color: ${$textColor};
        }
      `
  }}
  }
`

const HeaderBannerLeadingIcon = styled.img`
  height: ${rem(44)};
  width: auto;
  max-width: 132px;
  object-fit: contain;
  display: block;
`

const HeaderBannerInlineCta = styled(TextButton)`
  margin-left: ${rem(8)};
  min-width: 0;
  vertical-align: baseline;

  &.kind-tertiary, &.kind-secondary {
    height: auto;
  }
`

const HeaderBannerInlineCtaWithColor = styled(HeaderBannerInlineCta)<{
  $textColor: string
  $primaryContrastColor: string
}>`
  &&.kind-tertiary {
    padding: 0;
    --button-color: ${({ $textColor }) => $textColor};
    --button-hover-color: ${({ $textColor }) => $textColor};
    --button-focus-color: ${({ $textColor }) => $textColor};
    --button-active-color: ${({ $textColor }) => $textColor};
    --button-focus-ring-color: ${({ $textColor }) => $textColor};
    --button-hover-background-color: transparent;
    --button-focus-background-color: transparent;
    --button-active-background-color: transparent;
  }

  &&.kind-secondary {
    --button-color: ${({ $textColor }) => $textColor};
    --button-hover-color: ${({ $textColor }) => $textColor};
    --button-focus-color: ${({ $textColor }) => $textColor};
    --button-active-color: ${({ $textColor }) => $textColor};
    --button-border-color: ${({ $textColor }) => $textColor};
    --button-hover-border-color: ${({ $textColor }) => $textColor};
    --button-focus-border-color: ${({ $textColor }) => $textColor};
    --button-active-border-color: ${({ $textColor }) => $textColor};
    --button-hover-background-color: transparent;
    --button-focus-background-color: transparent;
    --button-active-background-color: transparent;
  }

  &&.kind-primary {
    --button-background-color: ${({ $textColor }) => $textColor};
    --button-border-color: ${({ $textColor }) => $textColor};
    --button-color: ${({ $primaryContrastColor }) => $primaryContrastColor};
    --button-hover-background-color: ${({ $textColor }) => $textColor};
    --button-hover-border-color: ${({ $textColor }) => $textColor};
    --button-hover-color: ${({ $primaryContrastColor }) => $primaryContrastColor};
    --button-focus-background-color: ${({ $textColor }) => $textColor};
    --button-focus-border-color: ${({ $textColor }) => $textColor};
    --button-focus-color: ${({ $primaryContrastColor }) => $primaryContrastColor};
    --button-active-background-color: ${({ $textColor }) => $textColor};
    --button-active-border-color: ${({ $textColor }) => $textColor};
    --button-active-color: ${({ $primaryContrastColor }) => $primaryContrastColor};
  }
`

export default function NotificationBannerV3() {
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()
  const campaign = useLabFeatureFlag(LabFeatureFlags.enableTopBannerMessage)
  const headerBanners = useAppSelector(state => state.system.features.headerBanners)
  const headerBannersRequestPath = useAppSelector(state => state.system.features.headerBannersRequestPath)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const distinctId = useAppSelector(state => state.auth.domainUserId ?? 'anonymous')
  const token = useAppSelector(state => state.auth.accessToken)
  const isHidden = useIsBannerHidden('notificationBannerV3')
  const isMobile = useScreenSizeOnly('mobile')
  const isSearchPage = useIsSearchPage()
  const isOfferPage = useIsOfferPage()
  const takeoverBannerLocation = getTakeoverBannerLocation(isSearchPage, isOfferPage)
  const shouldShowTakeoverBanner = useShouldShowBanner({ location: takeoverBannerLocation })
  const currentPath = useMemo(() => removeRegionCode(pathname, currentRegionCode), [pathname, currentRegionCode])

  useEffect(() => {
    if (shouldShowTakeoverBanner) {
      return
    }
    dispatch(fetchLabHeaderBanners(currentPath, distinctId, token))
  }, [dispatch, currentPath, distinctId, token, shouldShowTakeoverBanner])

  const dismissedBannerIds = getDismissedBannerIds()
  const headerBanner = useMemo(() => {
    if (headerBannersRequestPath && headerBannersRequestPath !== currentPath) {
      return undefined
    }
    return getRenderableHeaderBanner(headerBanners, dismissedBannerIds)
  }, [headerBanners, dismissedBannerIds, headerBannersRequestPath, currentPath])

  const banner = useMemo(() => {
    if (campaign?.enabled && isBannerMessagePayload(campaign.value) && !dismissedBannerIds.has(campaign.value.key)) {
      return campaign.value
    }
    return undefined
  }, [campaign, dismissedBannerIds])

  // TODO
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    if (headerBanner) {
      setIsDismissed(isBannerDismissed(headerBanner.id))
      return
    }

    if (banner) {
      setIsDismissed(isBannerDismissed(banner.key))
    }
  }, [banner, headerBanner])

  useEffect(() => {
    if (banner || headerBanner) {
      const bannerId = headerBanner?.id ?? banner?.key
      if (!bannerId) {
        return
      }

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setIsDismissed(isBannerDismissed(bannerId))
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [banner, headerBanner])

  const containerRef = useRef<HTMLDivElement>(null)

  const showModal = useContext(ModalContext)

  const openBannerModal = useCallback(() => {
    if (banner) {
      showModal(<NotificationBannerModal title={banner.modalTitle} content={banner.modalContent} />)
    }
  }, [banner, showModal])

  const dismiss = useCallback(() => {
    if (headerBanner) {
      setIsDismissed(true)
      recordSiteBannerDismissal(headerBanner.id, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
      return
    }

    if (banner) {
      setIsDismissed(true)
      // Dismiss endDate in BANNER_DISMISSAL_PERIOD_DAYS days from now
      recordSiteBannerDismissal(banner.key, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
    }
  }, [banner, headerBanner])

  if (shouldShowTakeoverBanner || isDismissed || isHidden) {
    return null
  }

  if (headerBanner) {
    const { kind, isCustomAppearance, description, cta, background, textColor, ctaPrimaryTextColor, iconImageId } = getHeaderBannerPresentation(headerBanner, isMobile)
    const backgroundValue = background.mode === 'image'
      ? getSimpleImageUrl(background.imageId)
      : background.value
    const iconImageUrl = iconImageId
      ? getImageUrl(iconImageId, { height: 88, quality: 'good' })
      : undefined
    return <HeaderBannerPageBanner
      ref={containerRef}
      id="notification-page-banner"
      className="no-print"
      kind={kind}
      icon={iconImageUrl ? <HeaderBannerLeadingIcon src={iconImageUrl} alt="" aria-hidden="true" /> : undefined}
      density="super-compact"
      onDismiss={dismiss}
      tabletAlign="center"
      $backgroundMode={background.mode}
      $backgroundValue={backgroundValue}
      $textColor={textColor}
      $isCustomAppearance={isCustomAppearance}
    >
      {renderHeaderBannerRichText(description)}
      {cta && <HeaderBannerInlineCtaWithColor
        to={cta.path}
        kind={cta.style}
        size="small"
        $textColor={textColor}
        $primaryContrastColor={ctaPrimaryTextColor}
      >
        {cta.label}
      </HeaderBannerInlineCtaWithColor>}
    </HeaderBannerPageBanner>
  }

  if (!banner) {
    return null
  }

  return <PageBanner
    ref={containerRef}
    id="notification-page-banner"
    className="no-print"
    kind={getBannerKind(banner.bannerType)}
    icon={getBannerIcon(banner.bannerType)}
    onDismiss={dismiss}
    tabletAlign="center"
    primaryActionButtonProps={banner.modalContent ? { onClick: openBannerModal, children: intl.formatMessage({ id: 'qimyJfMsL0jByto0bvax', defaultMessage: 'Read more', description: 'Button text to open modal with more details dialog window' }) } : undefined}
  >
    <b>{banner.boldText}</b> {banner.bannerText}
  </PageBanner>
}
